import { createHttpClient, normalizeRequestError } from './http'
import { validShareReadToken } from '@/utils/share-session-token'

export function isSharedReadRequest (config) {
  const method = (config.method || 'get').toLowerCase(), url = config.url || ''
  if (config.baseURL) return false
  if (method === 'get') return /^\/jetlinks\/visualization\/share\/[a-f0-9]{64}\/runtime$/.test(url) ||
    /^\/jetlinks\/device-instance\/[^/?#]+\/(detail|property\/[^/?#]+)$/.test(url)
  if (method !== 'post') return false
  return /^\/jetlinks\/visualization\/share\/[a-f0-9]{64}\/_session$/.test(url) ||
    url === '/jetlinks/visualization/device-locations/_query' ||
    url === '/jetlinks/visualization/device-status/_query' ||
    url === '/jetlinks/visualization/fleet/_query' ||
    url === '/jetlinks/visualization/charging-statistics/_query' ||
    /^\/jetlinks\/(device-product\/_query|device-instance\/_(query|count))$/.test(url) ||
    /^\/jetlinks\/device-instance\/[^/?#]+\/(property\/[^/?#]+|agg)\/_query$/.test(url)
}

export function createSharedReadClient (token, current, onUnauthorized) {
  const client = createHttpClient({ getToken: () => token, onUnauthorized })
  client.interceptors.request.use(config => {
    if (!current()) throw new Error('分享访问身份已变化，请重新验证')
    if (!isSharedReadRequest(config)) throw new Error('分享仅允许原生产品、设备和属性只读请求')
    // 清除可能继承的旧账号头，随后由本客户端认证拦截器写入捕获的实际访问者令牌。
    for (const headers of [config.headers, config.headers.common, config.headers[config.method]]) {
      if (headers) Object.keys(headers).filter(key => ['x-access-token', 'authorization'].includes(key.toLowerCase())).forEach(key => { delete headers[key] })
    }
    return config
  })
  client.interceptors.response.use(response => {
    if (!current()) throw new Error('分享会话或身份已失效，请重新验证')
    return response
  })
  return client
}

// 数据与WS不借用浏览器登录态；仅固定密码验证入口携带非秘密防刷Cookie。
// URL、方法和会话均由闭包固定，组件只能发起既有的原生只读请求。
export function createDelegatedReadClient ({ code, session = '', current, onUnauthorized = () => {}, fetcher = (...args) => fetch(...args) }) {
  if (!/^[a-f0-9]{64}$/.test(code) || (session && !validShareReadToken(session))) throw new Error('分享会话无效，请重新验证')
  const base = '/jetlinks/visualization/share/' + code
  return async config => {
    if (!current()) throw new Error('分享会话已失效，请重新验证')
    const method = (config.method || 'get').toLowerCase(), original = config.url || ''
    const bootstrap = method === 'get' && original === base
    const access = method === 'post' && original === base + '/_session'
    const runtime = method === 'get' && original === base + '/runtime'
    const native = (!original.startsWith('/jetlinks/visualization/') || ['/jetlinks/visualization/device-locations/_query', '/jetlinks/visualization/device-status/_query', '/jetlinks/visualization/fleet/_query', '/jetlinks/visualization/charging-statistics/_query'].includes(original)) && isSharedReadRequest(config)
    if (config.baseURL || config.params || (!bootstrap && !access && !runtime && !native) || ((runtime || native) && !session)) throw new Error('分享仅允许当前链接的原生产品、设备和属性只读请求')
    if (native && original.split('/').some(segment => {
      try { const decoded = decodeURIComponent(segment); return /[\\/?#%]/.test(decoded) || Array.from(decoded).some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127) || ['.', '..'].includes(decoded) } catch (error) { return true }
    })) throw new Error('分享读取地址无效')
    const url = native ? base + '/data/' + original.slice('/jetlinks/'.length) : original
    const controller = new AbortController(), cancelToken = config.cancelToken
    let cancellation, timer
    if (cancelToken) {
      cancelToken.throwIfRequested()
      // 兼容项目的 Axios 0.21 CancelToken；取消后仍保留原取消异常供调用方识别。
      cancelToken.promise.then(cause => { cancellation = cause; controller.abort() })
    }
    const abort = () => controller.abort()
    if (config.signal) { if (config.signal.aborted) abort(); else config.signal.addEventListener('abort', abort, { once: true }) }
    timer = setTimeout(() => controller.abort(), 30000)
    try {
      const headers = { Accept: 'application/json', ...(session && !bootstrap && !access ? { 'X-Visualization-Session': session } : {}) }
      const body = method === 'post' ? JSON.stringify(config.data === undefined ? {} : config.data) : undefined
      if (body !== undefined) headers['Content-Type'] = 'application/json'
      const response = await fetcher(url, { method: method.toUpperCase(), headers, body, credentials: access ? 'same-origin' : 'omit', cache: 'no-store', redirect: 'error', signal: controller.signal })
      let data
      try { data = await response.json() } catch (error) { if (!response.ok) throw { response: { status: response.status, data: {} } }; throw new Error('分享接口返回格式无效，请重新验证') }
      if (!current()) throw new Error('分享会话已失效，请重新验证')
      if (!response.ok) throw { response: { status: response.status, data, headers: response.headers } }
      return { status: response.status, data, config: { url, method } }
    } catch (cause) {
      if (cancellation) throw cancellation
      const error = cause && cause.response ? normalizeRequestError(cause) : cause && cause.name === 'AbortError' ? normalizeRequestError({ code: 'ECONNABORTED' }) : cause instanceof TypeError ? normalizeRequestError(cause) : cause
      if (error.status === 401) onUnauthorized(error)
      throw error
    } finally { clearTimeout(timer); if (config.signal) config.signal.removeEventListener('abort', abort) }
  }
}
